/**
 * useAnalyticsWorkspaceMembers Hook
 * 
 * Fetches workspace members for Analytics page.
 * Handles loading state and error handling.
 */

import React, { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface WorkspaceMember {
  id: string;
  name?: string | undefined;
  email?: string | undefined;
  role?: string | undefined; // Role from members subcollection (owner, admin, user, viewer)
}

export interface UseAnalyticsWorkspaceMembersParams {
  workspaceId: string;
}

export interface UseAnalyticsWorkspaceMembersResult {
  workspaceMembers: WorkspaceMember[];
  membersLoaded: boolean;
  selectedUsers: string[];
  setWorkspaceMembers: React.Dispatch<React.SetStateAction<WorkspaceMember[]>>;
  setMembersLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
}

/**
 * Hook for fetching workspace members for Analytics
 */
export const useAnalyticsWorkspaceMembers = ({
  workspaceId,
}: UseAnalyticsWorkspaceMembersParams): UseAnalyticsWorkspaceMembersResult => {
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [membersLoaded, setMembersLoaded] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Safety net: Always filter out orphaned user IDs and users without name/email whenever workspaceMembers changes
  useEffect(() => {
    const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
    const BYPASS_USER_ID = "bypass-user";
    // Get owner ID from workspaceMembers (owner should always be included)
    const ownerId = workspaceMembers.find(u => u.role === "owner")?.id;
    
    const hasOrphaned = workspaceMembers.some(user => 
      isOrphanedUserId(user.id) || 
      (user.id !== BYPASS_USER_ID && user.id !== ownerId && !user.name && !user.email)
    );
    
    if (hasOrphaned) {
      const filtered = workspaceMembers.filter(user => {
        // Always include owner and bypass-user
        if (user.id === ownerId || user.id === BYPASS_USER_ID) {
          return true;
        }
        // Filter out orphaned IDs
        if (isOrphanedUserId(user.id)) {
          return false;
        }
        // For other users, they must have either name or email
        return !!(user.name || user.email);
      });
      
      if (filtered.length !== workspaceMembers.length) {
        const removedIds = workspaceMembers
          .filter(u => !filtered.includes(u))
          .map(u => u.id);
        console.warn(`[Analytics] Safety net: Found orphaned user(s) in workspaceMembers, removing:`, removedIds);
        setWorkspaceMembers(filtered);
        // Also remove from selectedUsers if they're there
        const filteredSelected = selectedUsers.filter(id => 
          !isOrphanedUserId(id) && filtered.some(u => u.id === id)
        );
        if (filteredSelected.length !== selectedUsers.length) {
          setSelectedUsers(filteredSelected);
        }
      }
    }
  }, [workspaceMembers, selectedUsers]);

  useEffect(() => {
    if (!workspaceId || !auth.currentUser) return;

    const currentUser = auth.currentUser;
    
    // Initialize with current user immediately so button appears right away
    // This prevents layout shift and makes the button appear with other filters
    const initialUser: WorkspaceMember = {
      id: currentUser.uid,
      name: currentUser.displayName || undefined,
      email: currentUser.email || undefined,
      role: undefined, // Will be set when members are loaded
    };
    setWorkspaceMembers([initialUser]);
    setMembersLoaded(true); // Mark as loaded so button appears immediately
    
    // Initialize with empty array - will be set to all users when members load
    setSelectedUsers([]); // Will be set to all users when members load

    const fetchWorkspaceMembers = async (): Promise<void> => {
      try {
        // Known orphaned user IDs to exclude - defined once at the top
        // Check if ID starts with "Ao7fhBrt" to catch variations like "Ao7fhBrt7jh2vCTd9CYpqLoWLT63"
        const isOrphanedUserId = (userId: string): boolean => {
          return userId.startsWith("Ao7fhBrt");
        };
        
        // Special user IDs - defined at function scope
        // Note: We use bypass-user instead of dev-user-developer now
        const BYPASS_USER_ID = "bypass-user";
        
        // First, fetch the workspace to get the owner
        const workspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
        const ownerId = workspaceDoc.exists() ? (workspaceDoc.data()?.ownerId as string | undefined) : undefined;
        
        // Collect all user IDs: owner + members
        // Use Set to ensure unique IDs, but keep all users even if they have the same name
        const allUserIds = new Set<string>();
        
        // Add owner if exists and is not the workspace ID itself
        if (ownerId && ownerId !== workspaceId) {
          allUserIds.add(ownerId);
        }
        
        // Fetch members from workspace subcollection
        // Handle permission errors gracefully
        // Store member data immediately for fast display
        const memberDataMap = new Map<string, { name?: string; displayName?: string; email?: string; role?: string }>();
        let membersSnapshot;
        try {
          const membersRef = collection(db, "workspaces", workspaceId, "members");
          membersSnapshot = await getDocs(membersRef);
          
          console.log(`[Analytics] Fetched ${membersSnapshot.docs.length} members from subcollection`);
          
          membersSnapshot.docs.forEach((docSnap) => {
            const memberUserId = docSnap.id;
            // Filter out dev-user-developer - we use bypass-user now
            if (memberUserId === "dev-user-developer") {
              console.log(`[Analytics] Skipping dev-user-developer (replaced with bypass-user): ${memberUserId}`);
              return;
            }
            // Ensure we don't add the workspace ID as a user ID
            if (memberUserId && memberUserId !== workspaceId) {
              allUserIds.add(memberUserId);
              
              // Extract member data immediately for fast initial display
              const memberData = docSnap.data();
              memberDataMap.set(memberUserId, {
                name: memberData.name as string | undefined,
                displayName: memberData.displayName as string | undefined,
                email: memberData.email as string | undefined,
                role: (memberData.role as string | undefined) || "user",
              });
              
              // Debug: Log bypass-user specifically (only in bypass mode)
              if (memberUserId === BYPASS_USER_ID && isBypassAuthEnabled()) {
                console.log(`✅ [Analytics] Found bypass-user in members subcollection:`, {
                  id: memberUserId,
                  name: memberData.name,
                  email: memberData.email,
                  displayName: memberData.displayName,
                  role: memberData.role
                });
              }
            } else if (memberUserId === workspaceId) {
              console.warn(`[Analytics] Skipping invalid member ID (matches workspace ID): ${memberUserId}`);
            }
          });
          
          // Only expect bypass-user in dev bypass workspace; skip check when user is logged in or on demo workspace
          if (isBypassAuthEnabled() && !allUserIds.has(BYPASS_USER_ID)) {
            try {
              const bypassMemberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", BYPASS_USER_ID));
              if (bypassMemberDoc.exists()) {
                const bypassData = bypassMemberDoc.data();
                allUserIds.add(BYPASS_USER_ID);
                memberDataMap.set(BYPASS_USER_ID, {
                  name: bypassData.name as string | undefined,
                  displayName: bypassData.displayName as string | undefined,
                  email: bypassData.email as string | undefined,
                  role: (bypassData.role as string | undefined) || "user",
                });
                console.log(`✅ [Analytics] Successfully added bypass-user from direct fetch:`, bypassData);
              }
            } catch (bypassError) {
              console.error(`[Analytics] Error fetching bypass-user directly:`, bypassError);
            }
          }
        } catch (membersError: any) {
          console.error(`[Analytics] Error fetching members collection:`, membersError);
          // Continue with just the owner if we can't fetch members
          // This might happen due to permission issues, but we can still show the owner
        }
        
        // Always ensure current user is included
        if (currentUser && currentUser.uid && currentUser.uid !== workspaceId) {
          allUserIds.add(currentUser.uid);
        }
        
        // Validate: ensure workspace ID is not in the set
        if (allUserIds.has(workspaceId)) {
          console.error(`[Analytics] ERROR: Workspace ID found in user IDs! Removing it.`);
          allUserIds.delete(workspaceId);
        }
        
        // Explicitly exclude known orphaned user IDs and dev-user-developer (check all IDs in the set)
        const idsToRemove: string[] = [];
        allUserIds.forEach(userId => {
          if (isOrphanedUserId(userId)) {
            idsToRemove.push(userId);
          }
          // Remove dev-user-developer - we use bypass-user now
          if (userId === "dev-user-developer") {
            idsToRemove.push(userId);
          }
        });
        if (idsToRemove.length > 0) {
          console.warn(`[Analytics] Excluding ${idsToRemove.length} user(s):`, idsToRemove);
        }
        idsToRemove.forEach(id => allUserIds.delete(id));
        
        // Fetch user details for each user (owner + members)
        // Also fetch role from members subcollection
        // IMPORTANT: We fetch ALL users by their unique IDs, even if they have duplicate names
        // IMPORTANT: Include users even if they don't have a document in "users" collection
        // IMPORTANT: Filter out workspace ID if it somehow got added
        const validUserIds = Array.from(allUserIds).filter(userId => userId && userId !== workspaceId);
        
        // Debug: Log all user IDs before processing
        console.log(`[Analytics] Processing ${validUserIds.length} user IDs:`, {
          userIds: Array.from(validUserIds),
          hasBypassUser: validUserIds.includes(BYPASS_USER_ID),
          memberDataMapKeys: Array.from(memberDataMap.keys())
        });
        
        // Use member data we already have (from members collection) - no need to fetch member docs again
        // Only fetch user docs to get more complete info (name, email from users collection)
        const userPromises = validUserIds.map(async (userId: string): Promise<WorkspaceMember | null> => {
          // Double-check: skip if this is the workspace ID
          if (userId === workspaceId) {
            console.warn(`[Analytics] Skipping workspace ID as user ID: ${userId}`);
            return null;
          }
          
          // Explicitly exclude known orphaned user IDs
          if (isOrphanedUserId(userId)) {
            return null;
          }
          
          // Skip dev-user-developer - we use bypass-user now
          if (userId === "dev-user-developer") {
            console.log(`[Analytics] Skipping dev-user-developer (replaced with bypass-user): ${userId}`);
            return null;
          }
          
          // Get member data we already fetched (fast - no network call needed)
          const memberInfo = memberDataMap.get(userId);
          let role: string | undefined = memberInfo?.role;
          // Prefer displayName from members subcollection, then name, then email
          let memberName: string | undefined = memberInfo?.displayName || memberInfo?.name;
          let memberEmail: string | undefined = memberInfo?.email;
          
          // Set role for owner
          if (userId === ownerId) {
            role = "owner";
          } else if (!role) {
            role = "user"; // Default role
          }
          
          // For non-owner, non-bypass users: verify they exist in members collection
          // If they don't exist in memberDataMap, they were deleted and should be excluded
          // Always include bypass-user (special system user in dev mode)
          if (userId !== ownerId && userId !== BYPASS_USER_ID && !memberInfo) {
            return null;
          }
          
          // Only fetch user doc (we already have member data, so skip member doc fetch)
          // This is much faster - one less network call per user!
          let userDocResult;
          try {
            userDocResult = await getDoc(doc(db, "users", userId));
          } catch (userError: any) {
            console.error(`[Analytics] Error fetching user doc for ${userId}:`, userError);
            userDocResult = null;
          }
          
          // Always include bypass-user and owner (already checked memberInfo above)
          if (userId === BYPASS_USER_ID || userId === ownerId) {
            // Process user doc result - use it to enhance member data
            let user: WorkspaceMember | null = null;
            
            if (userDocResult && userDocResult.exists()) {
              const userData = userDocResult.data();
              // Prefer user doc data (more complete), but fallback to member data
              // Priority: userData.name > userData.displayName > memberName (which already includes member displayName)
              user = { 
                id: userId, 
                name: (userData.name as string | undefined) || (userData.displayName as string | undefined) || memberName,
                email: (userData.email as string | undefined) || memberEmail,
                role: role || "user", // Ensure role is always set
              };
            } else {
              // User document doesn't exist, use member data we already have
              // memberName already includes displayName from members subcollection
              let defaultName: string;
              if (userId === BYPASS_USER_ID) {
                defaultName = "Bypass-User";
              } else {
                defaultName = `User ${userId.substring(0, 8)}`;
              }
              user = { 
                id: userId, 
                name: memberName || memberEmail || defaultName,
                email: memberEmail,
                role: role || "user", // Ensure role is always set
              };
            }
            
            return user;
          }
          
          // For other users, check if they're orphaned (match cleanup logic - more aggressive)
          // memberInfo check already done above, so we know user exists in members collection
          
          if (userDocResult && userDocResult.exists()) {
            const userData = userDocResult.data();
            const userHasData = !!(userData?.name || userData?.email || userData?.displayName);
            
            // More aggressive: if user doc exists but is empty, exclude (orphaned)
            // This matches the cleanup logic which considers empty user docs as orphaned
            if (!userHasData) {
              return null;
            }
            
            // User has data - include
            return {
              id: userId,
              name: (userData.name as string | undefined) || (userData.displayName as string | undefined) || memberName,
              email: (userData.email as string | undefined) || memberEmail,
              role: role || "user",
            };
          } else {
            // User document doesn't exist
            // Always include bypass-user even if it has no name/email (special system user)
            if (userId === BYPASS_USER_ID && memberInfo) {
              return {
                id: userId,
                name: memberName || memberEmail || "Bypass-User",
                email: memberEmail,
                role: role || "user",
              };
            }
            // Include if member has name/email (valid member data) AND member exists in collection
            if ((memberName || memberEmail) && memberInfo) {
              return {
                id: userId,
                name: memberName || memberEmail || `User ${userId.substring(0, 8)}`,
                email: memberEmail,
                role: role || "user",
              };
            } else {
              // Orphaned user - exclude from list
              return null;
            }
          }
        });

        const usersListRaw = await Promise.all(userPromises);
        let usersList: WorkspaceMember[] = usersListRaw.filter((user): user is WorkspaceMember => user !== null);
        
        // Final safety check: explicitly remove known orphaned users from the final list
        usersList = usersList.filter(user => !isOrphanedUserId(user.id));
        
        // Additional safety check: remove users without proper name/email (orphaned members)
        // These are users that exist in workspaceMembers but have no meaningful data
        // They would show as "User XXXX" which indicates they're orphaned
        usersList = usersList.filter(user => {
          // Always include owner and bypass-user (they're special cases)
          if (user.id === ownerId || user.id === BYPASS_USER_ID) {
            return true;
          }
          // For other users, they must have either name or email
          return !!(user.name || user.email);
        });
        
        // Ensure current user is in the list (fallback if not found)
        // But exclude known orphaned users
        if (currentUser && !isOrphanedUserId(currentUser.uid)) {
          const currentUserId = currentUser.uid;
          if (!usersList.find((u) => u.id === currentUserId)) {
            const currentUserDoc = await getDoc(doc(db, "users", currentUserId));
            let role: string | undefined = undefined;
            if (currentUserId === ownerId) {
              role = "owner";
            } else {
              try {
                const memberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", currentUserId));
                if (memberDoc.exists()) {
                  const memberData = memberDoc.data();
                  role = memberData.role as string | undefined;
                }
              } catch (error) {
                console.error(`[Analytics] Error fetching member doc for current user:`, error);
              }
            }
            
            // Default to "user" if no role found
            if (!role) {
              role = "user";
            }
            
            if (currentUserDoc.exists()) {
              const userData = currentUserDoc.data();
              usersList.push({
                id: currentUserId,
                name: (userData.name as string | undefined) || (userData.displayName as string | undefined) || currentUser.displayName || undefined,
                email: userData.email as string | undefined || currentUser.email || undefined,
                role: role,
              });
            } else {
              // Fallback: use auth user data
              usersList.push({
                id: currentUserId,
                name: currentUser.displayName || undefined,
                email: currentUser.email || undefined,
                role: role,
              });
            }
          }
        }
        
        // Final safety check: ensure no orphaned users made it through (multiple layers of protection)
        const finalUsersList = usersList.filter(user => {
          if (isOrphanedUserId(user.id)) {
            console.warn(`[Analytics] CRITICAL: Orphaned user ${user.id} made it through all filters! Removing from final list.`);
            return false;
          }
          return true;
        });
        
        // ABSOLUTE FINAL FILTER: Remove any ID starting with "Ao7fhBrt" - this will catch it no matter what
        const cleanedUsersList = finalUsersList.filter(user => !isOrphanedUserId(user.id));
        
        // Set all fetched users - no filtering by name, all users are included
        // Each user has a unique ID, so even if names are duplicate, all users will be shown
        setWorkspaceMembers(cleanedUsersList);
        setMembersLoaded(true);

        // Initialize selected users - empty array means "show all users" (no filter)
        // This matches Activity page behavior: empty array = show all, specific IDs = filter to those users
        // Always start with empty array to show all users by default
        setSelectedUsers([]);
      } catch (error) {
        console.error("Error fetching workspace members:", error);
        setMembersLoaded(true); // Set to true even on error to prevent infinite loading
      }
    };

    // Call fetch immediately - don't wait
    fetchWorkspaceMembers();
  }, [workspaceId]);

  return {
    workspaceMembers,
    membersLoaded,
    selectedUsers,
    setWorkspaceMembers,
    setMembersLoaded,
    setSelectedUsers,
  };
};
